import { ItemSettingsService } from '../../services/ItemSettingsService';
import {
	formatAutoDecimals,
	formatCurrency as formatCurrencyBase,
	formatNumber as formatNumberBase,
	formatPercentage as formatPercentageBase,
} from '../../utils/formatters';

export interface FormatOptions {
	numberFormat?: 'auto' | 'number' | 'currency' | 'percent' | 'decimal';
	decimalPlaces?: number;
	percentValueIsWholeNumber?: boolean;
	currencySymbol?: string;
	currency?: string;
	currencyDisplay?: 'symbol' | 'code' | 'name';
	locale?: string;
	showPositiveColor?: boolean;
	textAlign?: 'left' | 'right' | 'center';
	fontWeight?: 'normal' | 'bold' | 'semibold' | 'medium';
}

/**
 * Format a number value based on the provided format options
 * This function bridges the gap between the list-builder's formatOptions
 * and the core formatting functions
 */
export function formatNumberWithFormatOptions(
	value: any,
	formatOptions?: FormatOptions,
): string {
	// Handle null/undefined values
	if (value == null || value === '') {
		return '-';
	}

	// Convert to number if it's a string
	const numValue = typeof value === 'string' ? parseFloat(value) : value;

	// Check if it's a valid number
	if (isNaN(numValue) || !isFinite(numValue) || typeof numValue !== 'number') {
		return '-';
	}

	const options = formatOptions || {};
	const locale = options.locale || 'en-US';
	// Clamp decimals to valid range for toFixed (0-100)
	let decimals = options.decimalPlaces ?? 2;
	if (!isFinite(decimals) || decimals < 0) {
		decimals = 2;
	} else if (decimals > 100) {
		decimals = 100;
	}

	switch (options.numberFormat) {
		case 'auto':
			// Strip trailing zeros: show only significant decimal digits
			return formatAutoDecimals(numValue, false, 'USD', locale);

		case 'currency': {
			const currency = options.currency || 'USD';
			const currencyDisplay = options.currencyDisplay || 'symbol';
			// When decimalPlaces is not explicitly configured, use the
			// maxPriceDecimalPrecision preference for max fraction digits.
			if (options.decimalPlaces == null) {
				return formatCurrencyBase(numValue, {
					currency,
					decimals: 2,
					maxDecimals: ItemSettingsService.getMaxPriceDecimalPrecision(),
					locale,
					valueIfEmpty: '-',
					currencyDisplay,
				});
			}
			return formatCurrencyBase(
				numValue,
				currency,
				decimals,
				locale,
				'-',
				currencyDisplay,
			);
		}

		case 'percent': {
			// formatPercentageBase expects a decimal value (0.5 = 50%).
			// By default, raw values are whole numbers (50 = 50%), so divide by 100.
			// If percentValueIsWholeNumber is false, the value is already a decimal.
			const decimalValue =
				options.percentValueIsWholeNumber === false ? numValue : numValue / 100;
			return formatPercentageBase(decimalValue, decimals, locale);
		}

		case 'decimal':
		case 'number':
		default:
			// Use the formatNumber function from core
			return formatNumberBase(numValue, decimals, locale);
	}
}

// Additional number formatting utilities
export function formatNumberWithOptions(
	value: number,
	options?: Intl.NumberFormatOptions,
): string {
	if (isNaN(value) || !isFinite(value)) {
		return '-';
	}
	return new Intl.NumberFormat('en-US', options).format(value);
}
